import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAllBlogPosts } from '../../utils/firestore';
import BlogPostCard from '../../components/blog/BlogPostCard';
import HeroSection from '../../components/common/HeroSection';
import SEO from '../../components/seo/SEO';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch blog posts
  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const blogPosts = await getAllBlogPosts();
        setPosts(blogPosts);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(blogPosts.map(post => post.category))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  // Filter posts based on category and search term
  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nihal-primary"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <SEO 
        title="Diyetisyen Nihal Kamış - Beslenme ve Sağlık Blogu"
        description="Diyetisyen Nihal Kamış'ın güncel beslenme ve sağlık yazıları. Dengeli beslenme, sağlıklı yaşam tarzı, kilo yönetimi, diyet programları ve daha fazlası hakkında görüşleri."
        url="https://www.dytnihalkamis.com/blog"
        keywords="beslenme blogu, diyetisyen yazısı, sağlıklı yaşam blogu, kilo verme ipuçları, dengeli beslenme, diyetisyen makaleleri"
      />
      
      {/* Page Header */}
      <HeroSection 
        title="Blog Yazılarım" 
        subtitle="Sağlıklı yaşam için güncel bilgi ve ipuçları" 
      />

      <div className="section-padding bg-white">
        <div className="container">
          {/* Search and Filter Section */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="w-full md:w-1/3">
                <input
                  type="text"
                  placeholder="Yazı Ara..."
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="w-full md:w-auto">
                <select
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">Tüm Kategoriler</option>
                  {categories.map((category, index) => (
                    <option key={index} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          {filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <BlogPostCard post={post} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-nihal-dark mb-4">Blog bulunamadı.</h3>
              <p className="text-gray-600">Arama veya filtre kriterlerinizi değiştirmeyi deneyin.</p>
            </div>
          )}

          {/* Load More Button */}
          {filteredPosts.length > 9 && (
            <div className="text-center mt-12">
              <button className="btn btn-secondary">
                Daha Fazla Makale Yükle
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;